import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchLibraryItems, type LibrarySource } from '@/features/library/services/libraryService';
import { LibraryCard } from '@/features/library/components/LibraryCard';
import { ALL_SHELF_ID, ATTENTION_SHELF_ID, ShelfPanel } from '@/features/library/components/ShelfPanel';
import { SAVED_SHELF_ID } from '@/features/library/services/shelfService';
import type { LibraryItem } from '@/shared/types';
import { logger } from '@/shared/utils/logger';
import { useProcessing } from '@/app/providers/processing';
import { useCurrentUser } from '@/app/providers/user';
import { useShelves } from '@/features/library/hooks/useShelves';
import { listSavedContent, savedAsLibraryItem, type SavedContent } from '@/features/browse/services/savedContentService';
import { getWatchProgress, watchFraction, type WatchProgressMap } from '@/features/library/services/watchProgressService';
import { ShareVideoModal } from '@/features/sharing/components/ShareVideoModal';
import { listSharedWithMe, type SharedVideo } from '@/features/sharing/services/sharingApi';
import {
  Button,
  ChevronDownIcon,
  EmptyState,
  GridViewIcon,
  Input,
  ListViewIcon,
  Menu,
  MenuItem,
  SearchIcon,
  Segmented,
  ShareIcon,
  Spinner,
  cx,
} from '@/shared/ui';

type SortKey = 'recent' | 'title' | 'oldest';

const SORT_LABELS: Record<SortKey, string> = {
  recent: 'Recently added',
  title: 'Title',
  oldest: 'Oldest first',
};

/**
 * The two states where the video will not arrive unless the user does something:
 * a generation that failed and can be retried, and one holding a credit hold
 * while it waits to be confirmed.
 *
 * `processing` and `queued` are deliberately not here. They resolve on their
 * own, they are already reported in the rail, and a row that empties itself as
 * work finishes would flicker for every generation.
 */
function needsAttention(item: LibraryItem): boolean {
  return item.status === 'failed' || item.status === 'awaiting_confirmation';
}

export default function LibraryPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState<LibraryItem[]>([]);
  const [savedContent, setSavedContent] = useState<SavedContent[]>([]);
  const [sharedWithMe, setSharedWithMe] = useState<SharedVideo[]>([]);
  const [watchProgress, setWatchProgress] = useState<WatchProgressMap>({});
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState<LibrarySource>('api');
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<SortKey>('recent');
  const [view, setView] = useState<'grid' | 'list'>('grid');
  const [selectedShelf, setSelectedShelf] = useState<string>(ALL_SHELF_ID);
  // Lifted out of ShelfPanel so a card's `Move to shelf › Start a new shelf` can
  // open the rail's name field rather than being a menu item that does nothing.
  const [creatingShelf, setCreatingShelf] = useState(false);
  const [sharingItem, setSharingItem] = useState<LibraryItem | null>(null);

  const { jobs, deleteJob } = useProcessing();
  const { user } = useCurrentUser();
  const userId = user?.userId;

  // Shared cancellation token so the effect cleanup AND a manual retry both
  // cancel whatever load is in flight — prevents setState after unmount.
  const loadSignalRef = useRef<{ cancelled: boolean }>({ cancelled: true });
  // Mirrors whether a grid is currently shown, readable from the load closure
  // (which can't see fresh `items` state).
  const hasItemsRef = useRef(false);

  const load = useCallback(() => {
    if (!userId) {
      setLoading(false);
      return;
    }
    loadSignalRef.current.cancelled = true;
    const signal = { cancelled: false };
    loadSignalRef.current = signal;

    setLoading(true);
    fetchLibraryItems(userId)
      .then((result) => {
        if (signal.cancelled) return;
        // Don't blank an already-populated grid when a refresh comes back empty
        // due to an outage/cache-miss (only an 'api' result may legitimately be
        // empty). Keep the stale grid and flag it as cached instead.
        const keepStale = result.items.length === 0 && result.source !== 'api' && hasItemsRef.current;
        if (!keepStale) {
          setItems(result.items);
          hasItemsRef.current = result.items.length > 0;
        }
        setSource(keepStale ? 'cache' : result.source);
      })
      .catch(() => {
        if (!signal.cancelled) setSource('error');
      })
      .finally(() => {
        if (!signal.cancelled) setLoading(false);
      });
  }, [userId]);

  // Refresh the library only when a job actually transitions, not on every poll
  // tick — ProcessingProvider hands back a fresh `jobs` array every ~5s.
  const jobSignature = useMemo(() => jobs.map((job) => `${job.id}:${job.status}`).join('|'), [jobs]);

  // Clear the previous account's items the moment the user changes, so a failed
  // load for the new user can't fall back to showing the prior user's library.
  useEffect(() => {
    setItems([]);
    hasItemsRef.current = false;
  }, [userId]);

  useEffect(() => {
    load();
    return () => {
      loadSignalRef.current.cancelled = true;
    };
  }, [jobSignature, load]);

  useEffect(() => {
    if (!userId) {
      setSavedContent([]);
      setSharedWithMe([]);
      setWatchProgress({});
      return;
    }
    let active = true;
    void Promise.allSettled([listSavedContent(userId), getWatchProgress(userId), listSharedWithMe()])
      .then(([savedRead, progressRead, sharedRead]) => {
        if (!active) return;
        if (savedRead.status === 'fulfilled') setSavedContent(savedRead.value);
        if (progressRead.status === 'fulfilled') setWatchProgress(progressRead.value);
        if (sharedRead.status === 'fulfilled') setSharedWithMe(sharedRead.value);

        const failures = [savedRead, progressRead, sharedRead].filter(
          (result): result is PromiseRejectedResult => result.status === 'rejected',
        );
        if (failures.length > 0) {
          logger.error(
            'Could not load saved content, watch progress, or shared videos',
            failures.map((failure) => failure.reason),
          );
        }
      });
    return () => {
      active = false;
    };
  }, [userId]);

  // Generated videos plus anything saved from browse — one grid, as designed.
  const allItems = useMemo(() => {
    const savedIds = new Set(savedContent.map((entry) => entry.id));
    return [
      ...items.filter((item) => !savedIds.has(item.id)),
      ...savedContent.map(savedAsLibraryItem),
    ];
  }, [items, savedContent]);

  const allItemIds = useMemo(() => allItems.map((item) => item.id), [allItems]);
  const { shelves, membership, counts, addTo, removeFrom, toggleFavorite, create, rename, recolor, remove } =
    useShelves(allItemIds);

  const savedIdSet = useMemo(() => new Set(savedContent.map((entry) => entry.id)), [savedContent]);

  const visibleItems = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    const filtered = allItems
      .filter((item) => {
        if (selectedShelf === ALL_SHELF_ID) return true;
        /* Saved is derived, not a membership row. Items get there from browse
           through savedContentService, which never writes shelf membership —
           so filtering this built-in the same way as a user shelf left it
           permanently empty no matter how much had been saved. */
        if (selectedShelf === SAVED_SHELF_ID) return savedIdSet.has(item.id);
        // Derived from status, for the same reason.
        if (selectedShelf === ATTENTION_SHELF_ID) return needsAttention(item);
        return (membership[item.id] ?? []).includes(selectedShelf);
      })
      .filter((item) => {
        if (!normalizedQuery) return true;
        return (
          item.title.toLowerCase().includes(normalizedQuery) ||
          (item.description?.toLowerCase().includes(normalizedQuery) ?? false)
        );
      });

    const sorted = [...filtered];
    if (sort === 'title') {
      sorted.sort((a, b) => a.title.localeCompare(b.title));
    } else {
      // `b − a` is newest first, which is what "Recently added" means; only
      // "Oldest first" reverses it. Reading the comparator and the multiplier
      // as one expression is how these came out backwards to begin with.
      sorted.sort((a, b) => {
        const newestFirst = new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        return sort === 'recent' ? newestFirst : -newestFirst;
      });
    }
    return sorted;
  }, [allItems, membership, selectedShelf, query, sort, savedIdSet]);

  // Same reason the filter special-cases it: countsByShelf works off membership,
  // which Saved never has any of, so its badge read 0 forever.
  const shelfCounts = useMemo(
    () => ({ ...counts, [SAVED_SHELF_ID]: savedIdSet.size }),
    [counts, savedIdSet],
  );

  const attentionCount = useMemo(() => allItems.filter(needsAttention).length, [allItems]);

  /* Retrying the last failure empties the row, and the row then hides — leaving
     the grid filtered by a shelf that is no longer on screen and no way back to
     All but guessing. Selection follows the row out. */
  useEffect(() => {
    if (attentionCount === 0 && selectedShelf === ATTENTION_SHELF_ID) {
      setSelectedShelf(ALL_SHELF_ID);
    }
  }, [attentionCount, selectedShelf]);

  const shelvesForItem = useCallback(
    (itemId: string) => {
      const ids = new Set(membership[itemId] ?? []);
      return shelves.filter((shelf) => ids.has(shelf.id));
    },
    [membership, shelves],
  );

  const handleDeleted = useCallback((id: string) => {
    setItems((current) => {
      const next = current.filter((item) => item.id !== id);
      hasItemsRef.current = next.length > 0;
      return next;
    });
  }, []);

  // The full-page spinner / error block only take over when there is nothing to
  // show yet; background refreshes (driven by jobs) keep the grid visible.
  const showSpinner = loading && allItems.length === 0;
  const showError = !loading && source === 'error' && allItems.length === 0;
  const showCachedNotice = !loading && source === 'cache';

  const activeShelfName =
    selectedShelf === ALL_SHELF_ID
      ? 'All uploads'
      : selectedShelf === ATTENTION_SHELF_ID
        ? 'Needs attention'
        : (shelves.find((shelf) => shelf.id === selectedShelf)?.name ?? 'All uploads');

  return (
    <section className="library page--wide">
      <h1 className="library__title">Your library</h1>
      <ShareVideoModal
        open={Boolean(sharingItem)}
        videoId={sharingItem?.id ?? ''}
        title={sharingItem?.title ?? 'video'}
        onClose={() => setSharingItem(null)}
      />

      <div className="library__layout">
        <ShelfPanel
          shelves={shelves}
          counts={shelfCounts}
          totalCount={allItems.length}
          attentionCount={attentionCount}
          selectedId={selectedShelf}
          onSelect={setSelectedShelf}
          creating={creatingShelf}
          onCreatingChange={setCreatingShelf}
          onCreate={(name) => void create(name)}
          onRename={(id, name) => void rename(id, name)}
          onRecolor={(id, color) => void recolor(id, color)}
          onDelete={(id) => {
            void remove(id);
            if (selectedShelf === id) setSelectedShelf(ALL_SHELF_ID);
          }}
        />

        <div className="library__main">
          <div className="library__toolbar">
            <div className="library__heading">
              <h2 className="library__shelf-name">{activeShelfName}</h2>
              <span className="library__count">
                {visibleItems.length} {visibleItems.length === 1 ? 'item' : 'items'}
              </span>
            </div>

            <div className="library__controls">
              <Input
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search your library"
                aria-label="Search your library"
                iconLeft={<SearchIcon size={16} />}
                fieldClassName="library__search"
              />

              {/* A menu rather than a `<select>`, because the options are the
                  visible part of a sort control and a native option list cannot
                  be styled: the popup is drawn by the browser, so it arrived in
                  the OS's chrome — square, its own type, its own highlight —
                  next to an app that is none of those things. This one is
                  ordinary DOM and wears the same menu as the card and job menus
                  it sits beside. */}
              <Menu
                label="Sort"
                placement="bottom-end"
                trigger={
                  <Button size="sm" className="library__sort-trigger" iconRight={<ChevronDownIcon size={14} />}>
                    Sort: {SORT_LABELS[sort]}
                  </Button>
                }
              >
                {(Object.keys(SORT_LABELS) as SortKey[]).map((key) => (
                  <MenuItem key={key} selected={sort === key} onSelect={() => setSort(key)}>
                    {SORT_LABELS[key]}
                  </MenuItem>
                ))}
              </Menu>

              <Segmented
                label="View"
                size="sm"
                value={view}
                onChange={setView}
                options={[
                  { value: 'grid', label: <span className="sr-only">Grid</span>, icon: <GridViewIcon size={15} /> },
                  { value: 'list', label: <span className="sr-only">List</span>, icon: <ListViewIcon size={15} /> },
                ]}
              />
            </div>
          </div>

          {showCachedNotice ? (
            <div className="library__notice" role="status">
              <span>Couldn’t reach the server — showing cached items.</span>
              <Button size="sm" onClick={load}>
                Retry
              </Button>
            </div>
          ) : null}

          {sharedWithMe.length > 0 ? (
            <section className="library__shared" aria-labelledby="shared-with-me-title">
              <div className="library__shared-head">
                <h2 id="shared-with-me-title">Shared with me</h2>
                <span>{sharedWithMe.length}</span>
              </div>
              <div className="library__shared-grid">
                {sharedWithMe.map((shared) => (
                  <button
                    key={shared.shareId}
                    type="button"
                    className="library__shared-card"
                    onClick={() => navigate(`/shares/direct/${shared.shareId}`)}
                  >
                    <span className="library__shared-thumb">
                      {shared.thumbnailUrl ? <img src={shared.thumbnailUrl} alt="" loading="lazy" /> : <ShareIcon size={20} />}
                      <span aria-hidden>
                        <ShareIcon size={15} />
                      </span>
                    </span>
                    <span className="library__shared-title">{shared.title}</span>
                  </button>
                ))}
              </div>
            </section>
          ) : null}

          {showSpinner ? (
            <div className="library__loading">
              <Spinner size={26} label="Loading library" />
            </div>
          ) : showError ? (
            <div role="alert">
              <EmptyState
                icon={<SearchIcon size={26} />}
                title="Couldn’t load your library"
                hint="Please check your connection and try again."
                action={
                  <Button variant="secondary" onClick={load}>
                    Try again
                  </Button>
                }
              />
            </div>
          ) : visibleItems.length > 0 ? (
            <div className={cx('library__grid', view === 'list' && 'library__grid--list')}>
              {visibleItems.map((item) => (
                <LibraryCard
                  key={item.id}
                  item={item}
                  shelves={shelvesForItem(item.id)}
                  allShelves={shelves}
                  watched={watchFraction(watchProgress, item.id)}
                  saved={savedIdSet.has(item.id)}
                  onAddToShelf={(shelfId) => void addTo(item.id, shelfId)}
                  onRemoveFromShelf={(shelfId) => void removeFrom(item.id, shelfId)}
                  onToggleFavorite={() => void toggleFavorite(item.id)}
                  onShare={
                    item.status === 'completed' && Boolean(item.videoUrl) && !savedIdSet.has(item.id)
                      ? () => setSharingItem(item)
                      : undefined
                  }
                  // The card's menu item was never wired to anything; this opens
                  // the rail's name field, which is where shelves get made.
                  onCreateShelf={() => setCreatingShelf(true)}
                  /* Permanent deletion, from #67. The card owns the
                     confirmation — it already owns the one for cancelling a
                     generation — so what arrives here is the confirmed action.
                     Rejections propagate: the card needs them to keep the
                     dialog open and say so, rather than closing on a delete
                     that never happened. */
                  onDelete={async () => {
                    await deleteJob(item.id);
                    handleDeleted(item.id);
                  }}
                />
              ))}
            </div>
          ) : (
            <EmptyState
              icon={<SearchIcon size={26} />}
              title={allItems.length === 0 ? 'No videos yet' : 'No videos found'}
              hint={
                allItems.length === 0
                  ? 'Submit content to populate your library.'
                  : 'Try a different search, shelf, or filter.'
              }
            />
          )}
        </div>
      </div>
    </section>
  );
}
